/**
 * Role 18 — Statement of Cash Flows (Indirect) — calculated totals.
 *
 * Extracted verbatim from the former single-class `CalculationService`, which held
 * all 13 sheets' total calculations in one 5,557-line file. Each function is pure and
 * self-contained (no shared state, no `this`), so the split is a move, not a rewrite.
 * `CalculationService` remains the injectable entry point and delegates here.
 */

/**
 * Calculate all Role18 (520000 - SOCF - Indirect) totals based on form data
 *
 * @param formData - The form data object with control values (format: qname[member][period])
 * @param xbrlPrefix - The XBRL prefix ('ifrs-full' for MFRS, 'ifrs-smes' for MPERS)
 * @returns Object containing only the calculated total fields
 */
export function calculateTotals_520000(
  formData: Record<string, any>,
  xbrlPrefix: 'ifrs-full' | 'ifrs-smes',
): Record<string, any> {
  const calculatedTotals: Record<string, any> = {};

  // Helper function to get field value from formData
  const getFieldValue = (qname: string, suffix: string): number => {
    const value = formData[qname + suffix];

    // Handle null, undefined, or empty string
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    // Convert to number if it's a string
    const numValue = typeof value === 'number' ? value : parseFloat(value);

    // Return 0 if conversion resulted in NaN
    return isNaN(numValue) ? 0 : numValue;
  };

  // Helper function to set calculated value
  const setValue = (qname: string, suffix: string, value: number): void => {
    const controlName = qname + suffix;
    calculatedTotals[controlName] = value;
  };

  // Determine if we're using MFRS or MPERS
  const isMFRS = xbrlPrefix === 'ifrs-full';

  // Always calculate for all 4 columns
  // PHASE 2: Updated to bracket notation format
  const suffixes = [
    '[Consolidated][CurrentYear]',
    '[Consolidated][PreviousYear]',
    '[Separate][CurrentYear]',
    '[Separate][PreviousYear]',
  ];

  suffixes.forEach((suffix) => {
    if (isMFRS) {
      // Calculate totals for MFRS schema
      // 1. Aggregate Profit/Loss Before Tax (= ProfitLossBeforeTax)
      const profitLossBeforeTax = getFieldValue(
        'ifrs-full:ProfitLossBeforeTax',
        suffix,
      );
      setValue(
        'ssmt-mfrs:AggregateProfitLossBeforeTax',
        suffix,
        profitLossBeforeTax,
      );

      // 2. Total Impairment Loss (sum of all impairment fields)
      const totalImpairment =
        getFieldValue(
          'ifrs-full:ImpairmentLossRecognisedInProfitOrLossPropertyPlantAndEquipment',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:ImpairmentLossRecognisedInProfitOrLossInvestmentProperty',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:ImpairmentLossRecognisedInProfitOrLossOfPrepaidLeaseRental',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:ImpairmentLossRecognisedInProfitOrLossIntangibleAssetsOtherThanGoodwill',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:ImpairmentLossRecognisedInProfitOrLossOfInvestmentInSubsidiaries',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:ImpairmentLossRecognisedInProfitOrLossOfInvestmentsInAssociates',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:ImpairmentLossRecognisedInProfitOrLossOfInvestmentsInJointVentures',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:ImpairmentLossRecognisedInProfitOrLossOnOtherInvestments',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:ImpairmentLossRecognisedInProfitOrLossOnOtherFinancialAssetsOrReceivables',
          suffix,
        );
      setValue(
        'ifrs-full:ImpairmentLossRecognisedInProfitOrLoss',
        suffix,
        totalImpairment,
      );

      // 3. Total Gain/Loss on Disposal of Non-current Assets
      const totalDisposalGains =
        getFieldValue(
          'ifrs-full:GainsLossesOnDisposalsOfPropertyPlantAndEquipment',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:GainsLossesOnDisposalsOfInvestmentProperties',
          suffix,
        );
      setValue(
        'ifrs-full:GainsLossesOnDisposalsOfNoncurrentAssets',
        suffix,
        totalDisposalGains,
      );

      // 4. Total Write-offs
      const totalWriteoffs =
        getFieldValue(
          'ifrs-full:WritedownsReversalsOfPropertyPlantAndEquipment',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:WritedownsReversalsOfIntangibleAssets',
          suffix,
        ) +
        getFieldValue('ssmt-mfrs:WritedownsReversalsOfInvestments', suffix) +
        getFieldValue('ifrs-full:WritedownsReversalsOfInventories', suffix) +
        getFieldValue(
          'ssmt-mfrs:WritedownsOfTradeAndOtherReceivables',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:WritedownsReversalsOfOtherFinancialAssets',
          suffix,
        );
      setValue(
        'ssmt-mfrs:WritedownsReversalsOfAssets',
        suffix,
        totalWriteoffs,
      );

      // 5. Total Adjustments to Reconcile Profit/Loss (sum of all adjustment fields)
      // Note: Some fields are subtracted (gains, income) instead of added.
      // Total write-off, Write back of expenses, and Other adjustments to
      // reconcile profit (loss) are ADDED to the total — their former "(-)"
      // label prefix and subtraction were removed, so they now contribute
      // positively. This mirrors the f46 formula in
      // 18a_*/01_cash-flows-from-operating-activities.md and the form-JSON
      // calculation.formula (role-520000-mfrs.form.json).
      const totalAdjustments =
        getFieldValue('ifrs-full:AdjustmentsForDepreciationExpense', suffix) +
        getFieldValue('ifrs-full:AdjustmentsForAmortisationExpense', suffix) -
        getFieldValue('ifrs-full:AdjustmentsForDividendIncome', suffix) +
        getFieldValue('ifrs-full:AdjustmentsForSharebasedPayments', suffix) -
        getFieldValue(
          'ifrs-full:AdjustmentsForGainsLossesOnFairValueAdjustmentInvestmentProperty',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:AdjustmentsForUnrealisedForeignExchangeGainLoss',
          suffix,
        ) -
        getFieldValue('ifrs-full:AdjustmentsForInterestIncome', suffix) +
        getFieldValue(
          'ssmt-mfrs:AdjustmentsForPerformanceBasedEmployeeShareSchemeAndOtherExpenses',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:AdjustmentsForShareOfProfitsOfJointVentures',
          suffix,
        ) +
        getFieldValue('ssmt-mfrs:AdjustmentsForShareOptionExpenses', suffix) -
        getFieldValue(
          'ifrs-full:AdjustmentsForUndistributedProfitsOfAssociates',
          suffix,
        ) +
        getFieldValue('ifrs-full:AdjustmentsForFinanceCosts', suffix) -
        getFieldValue('ifrs-full:AdjustmentsForFinanceIncome', suffix) -
        getFieldValue(
          'ssmt-mfrs:PaymentsForLeasesOfLowValueAssetsAndVariableLeasePaymentsNotIncludedInMeasurementOfLeaseLiability',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:AdjustmentsForOtherNoncurrentAssets',
          suffix,
        ) +
        totalImpairment -
        // getFieldValue('ifrs-full:GainsLossesOnDisposalsOfPropertyPlantAndEquipment', suffix) +
        // getFieldValue('ifrs-full:GainsLossesOnDisposalsOfInvestmentProperties', suffix) -
        totalDisposalGains -
        getFieldValue(
          'ssmt-mfrs:GainsLossesOnDisposalOfSubsidiaries',
          suffix,
        ) -
        getFieldValue('ssmt-mfrs:GainsLossesOnDisposalOfAssociates', suffix) -
        getFieldValue(
          'ssmt-mfrs:GainsLossesOnDisposalOfJointVentures',
          suffix,
        ) -
        getFieldValue(
          'ssmt-mfrs:GainsLossesOnDisposalOfOtherInvestments',
          suffix,
        ) -
        getFieldValue(
          'ssmt-mfrs:RealisedGainTransferredFromEquityOnForeignCurrencyExchange',
          suffix,
        ) +
        getFieldValue('ifrs-full:OtherAdjustmentsForNoncashItems', suffix) +
        totalWriteoffs +
        getFieldValue('ssmt-mfrs:WriteBackOfExpenses', suffix) +
        getFieldValue(
          'ifrs-full:OtherAdjustmentsToReconcileProfitLoss',
          suffix,
        );
      setValue(
        'ifrs-full:AdjustmentsForReconcileProfitLoss',
        suffix,
        totalAdjustments,
      );

      // 6. Operating Surplus/Deficit Before Working Capital Changes
      const operatingSurplus = profitLossBeforeTax + totalAdjustments;
      setValue(
        'ifrs-full:CashFlowsFromUsedInOperationsBeforeChangesInWorkingCapital',
        suffix,
        operatingSurplus,
      );

      // 7. Total Changes in Working Capital
      // Negative-sum section: every working-capital adjustment carries the
      // `(-)` label prefix and is SUBTRACTED (inventories included). The
      // inventories term was previously additive, which understated the total
      // by 2× its value and raised a phantom extracted-sum Suggested Change
      // (form −5.83 vs extracted snapshot −8.67). See spec 18a working-capital fields.
      const totalWorkingCapitalChanges =
        -getFieldValue(
          'ifrs-full:AdjustmentsForDecreaseIncreaseInInventories',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:AdjustmentsForDecreaseIncreaseInTradeAndOtherReceivables',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:AdjustmentsForIncreaseDecreaseInTradeAndOtherPayables',
          suffix,
        ) -
        getFieldValue(
          'ssmt-mfrs:AdjustmentsForDecreaseIncreaseInPropertyDevelopmentCosts',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:AdjustmentsForDecreaseIncreaseInContractAssets',
          suffix,
        ) -
        getFieldValue(
          'ssmt:AdjustmentsForDecreaseIncreaseInContractLiabilities',
          suffix,
        );
      setValue(
        'ifrs-full:IncreaseDecreaseInWorkingCapital',
        suffix,
        totalWorkingCapitalChanges,
      );

      // 8. Cash Generated from Operations
      const cashGeneratedFromOps =
        operatingSurplus + totalWorkingCapitalChanges;
      setValue(
        'ifrs-full:CashFlowsFromUsedInOperations',
        suffix,
        cashGeneratedFromOps,
      );

      // 9. Net Cash from Operating Activities
      const netCashFromOperating =
        cashGeneratedFromOps + // row 88
        getFieldValue(
          'ssmt-mfrs:PaymentsForLeasesOfLowValueAssetsAndVariableLeasePaymentsNotIncludedInMeasurementOfLeaseLiability',
          suffix,
        ) + // row 45
        getFieldValue(
          'ifrs-full:DividendsReceivedClassifiedAsOperatingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:DividendsPaidClassifiedAsOperatingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:InterestReceivedClassifiedAsOperatingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:InterestPaidClassifiedAsOperatingActivities',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:PaymentsForRetirementBenefitsScheme',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:IncomeTaxesPaidRefundClassifiedAsOperatingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:OtherInflowsOutflowsOfCashClassifiedAsOperatingActivities',
          suffix,
        );
      setValue(
        'ifrs-full:CashFlowsFromUsedInOperatingActivities',
        suffix,
        netCashFromOperating,
      );

      // 10. Net Cash from Investing Activities (sum of all 30 investing fields)
      const netCashFromInvesting =
        getFieldValue(
          'ifrs-full:CashFlowsFromLosingControlOfSubsidiariesOrOtherBusinessesClassifiedAsInvestingActivities',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:CashFlowsUsedInObtainingControlOfSubsidiariesOrOtherBusinessesClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:ProceedsFromSalesOfInterestsInAssociates',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:OtherCashPaymentsToAcquireInterestsInAssociates',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:OtherCashReceiptsFromSalesOfInterestsInJointVenturesClassifiedAsInvestingActivities',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:OtherCashPaymentsToAcquireInterestsInJointVenturesClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:CashReceiptsFromRepaymentOfAdvancesAndLoansMadeToOtherPartiesClassifiedAsInvestingActivities',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:CashAdvancesAndLoansMadeToOtherPartiesClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:DepositPlacedWithInvestmentBrokers',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:OtherCashPaymentsToAcquireEquityOrDebtInstrumentsOfOtherEntitiesClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:OtherCashReceiptsFromSalesOfEquityOrDebtInstrumentsOfOtherEntitiesClassifiedAsInvestingActivities',
          suffix,
        ) -
        getFieldValue(
          'ssmt-mfrs:ProceedsFromDisposalOfNetCashAndCashEqivalentsDisposedOffFromDiscontinuingOperatrions',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:TaxPaidOnGainOnDisposalFromDiscontinuingOperations',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:ProceedsFromGovernmentGrantsClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:ProceedsFromSalesOfIntangibleAssetsClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:ProceedsFromOtherLongtermAssetsClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:ProceedsFromSalesOfPropertyPlantAndEquipmentClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:ProceedsFromSaleOfPrepaidLeasePayments',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:PurchaseOfIntangibleAssetsClassifiedAsInvestingActivities',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:PurchaseOfOtherLongtermAssetsClassifiedAsInvestingActivities',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:PurchaseOfPropertyPlantAndEquipmentClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:DividendsReceivedClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:InterestReceivedClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:ProceedsFromSalesOfInvestmentsOtherThanInvestmentsAccountedForUsingEquityMethod',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:PurchaseOfInvestmentsOtherThanInvestmentsAccountedForUsingEquityMethod',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:PaymentsForDevelopmentProjectExpenditure',
          suffix,
        ) -
        getFieldValue('ssmt-mfrs:NetRepaymentFromJointVentures', suffix) -
        getFieldValue('ssmt-mfrs:NetRepaymentFromAssociates', suffix) +
        getFieldValue(
          'ssmt-mfrs:CapitalDistributionsFromAssociates',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:OtherInflowsOutflowsOfCashClassifiedAsInvestingActivities',
          suffix,
        );
      setValue(
        'ifrs-full:CashFlowsFromUsedInInvestingActivities',
        suffix,
        netCashFromInvesting,
      );

      // 11. Net Cash from Financing Activities (sum of all 18 financing fields)
      const netCashFromFinancing =
        getFieldValue(
          'ssmt-mfrs:PaymentsForPrincipalPortionOfLeaseLiability',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mfrs:PaymentsForInterestPortionOfLeaseLiability',
          suffix,
        ) +
        getFieldValue('ifrs-full:ProceedsFromIssuingShares', suffix) +
        getFieldValue(
          'ssmt-mfrs:ProceedsFromIssueOfSharesInSubsidiaryToNoncontrollingInterests',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:ProceedsFromIssuingOtherEquityInstruments',
          suffix,
        ) +
        getFieldValue('ifrs-full:ProceedsFromExerciseOfOptions', suffix) -
        getFieldValue(
          'ifrs-full:PaymentsToAcquireOrRedeemEntitysShares',
          suffix,
        ) -
        getFieldValue(
          'ssmt-mfrs:PaymentsMadeToAcquireNoncontrollingInterest',
          suffix,
        ) -
        getFieldValue('ifrs-full:PaymentsOfOtherEquityInstruments', suffix) +
        getFieldValue(
          'ssmt-mfrs:CashPaymentsForIssuanceOfPerpetualSukuk',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:ProceedsFromIssueOfBondsNotesAndDebentures',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:RepaymentsOfBorrowingsClassifiedAsFinancingActivities',
          suffix,
        ) +
        getFieldValue('ssmt-mfrs:RepurchaseOfTreasuryShares', suffix) +
        getFieldValue('ssmt-mfrs:WithdrawalOfBankDeposits', suffix) +
        getFieldValue(
          'ssmt-mfrs:WithdrawalOfSecuritiesPledgedForBorrowings',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:DividendsPaidClassifiedAsFinancingActivities',
          suffix,
        ) -
        getFieldValue(
          'ifrs-full:InterestPaidClassifiedAsFinancingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-full:OtherInflowsOutflowsOfCashClassifiedAsFinancingActivities',
          suffix,
        );
      setValue(
        'ifrs-full:CashFlowsFromUsedInFinancingActivities',
        suffix,
        netCashFromFinancing,
      );

      // 12. Net Increase/Decrease Before Exchange Rate Effects
      const netIncreaseBeforeExchange =
        netCashFromOperating + netCashFromInvesting + netCashFromFinancing;
      setValue(
        'ifrs-full:IncreaseDecreaseInCashAndCashEquivalentsBeforeEffectOfExchangeRateChanges',
        suffix,
        netIncreaseBeforeExchange,
      );

      // 13. Net Increase/Decrease After Exchange Rate Effects
      const exchangeRateEffect = getFieldValue(
        'ifrs-full:EffectOfExchangeRateChangesOnCashAndCashEquivalents',
        suffix,
      );
      const netIncreaseAfterExchange =
        netIncreaseBeforeExchange + exchangeRateEffect;
      setValue(
        'ifrs-full:IncreaseDecreaseInCashAndCashEquivalents',
        suffix,
        netIncreaseAfterExchange,
      );

      // 14. Cash and Cash Equivalents Details (reconciliation)
      // Calculate CashAndCashEquivalents:End_occurrence2 from details
      // Formula: Cash and Bank Balances - Bank Overdrafts + Other Adjustments
      const cashAndBank = getFieldValue('ssmt:CashAndBankBalances', suffix);
      const bankOverdraft = getFieldValue(
        'ifrs-full:BankOverdraftsClassifiedAsCashEquivalents',
        suffix,
      );
      const otherAdjustments = getFieldValue(
        'ssmt:OtherAdjustmentsToReconcileCashAndCashEquivalents',
        suffix,
      );
      const cashAndCashEquivalentsAtEndOfPeriodDetails =
        cashAndBank - bankOverdraft + otherAdjustments;
      setValue(
        'ifrs-full:CashAndCashEquivalents:End_occurrence2',
        suffix,
        cashAndCashEquivalentsAtEndOfPeriodDetails,
      );
    } else {
      // Calculate totals for MPERS schema
      // 1. Aggregate Profit/Loss Before Tax (continuing + discontinued)
      const profitContinuing = getFieldValue(
        'ifrs-smes:ProfitLossBeforeTax',
        suffix,
      );
      const profitDiscontinued = getFieldValue(
        'ifrs-smes:ProfitLossFromDiscontinuedOperations',
        suffix,
      );
      const aggregateProfitLoss = profitContinuing + profitDiscontinued;
      setValue(
        'ssmt-mpers:AggregateProfitLossBeforeTax',
        suffix,
        aggregateProfitLoss,
      );

      // 2. Total Adjustments to Reconcile Profit/Loss (sum of 28 adjustment fields)
      // Note: Fields with Weight -1 in taxonomy are subtracted
      const totalAdjustments =
        getFieldValue(
          'ifrs-smes:AdjustmentsForNoncashIncomeTaxExpense',
          suffix,
        ) +
        getFieldValue('ifrs-smes:AdjustmentsForNoncashFinanceCosts', suffix) +
        getFieldValue('ifrs-smes:AdjustmentsForIncomeTaxExpense', suffix) +
        getFieldValue('ifrs-smes:AdjustmentsForFinanceCosts', suffix) -
        getFieldValue('ssmt-mpers:AdjustmentsForFinanceIncome', suffix) +
        getFieldValue(
          'ifrs-smes:AdjustmentsForDecreaseIncreaseInInventories',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:AdjustmentsForDecreaseIncreaseInTradeAccountReceivable',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:AdjustmentsForDecreaseIncreaseInOtherOperatingReceivables',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:AdjustmentsForIncreaseDecreaseInTradeAccountPayable',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:AdjustmentsForIncreaseDecreaseInOtherOperatingPayables',
          suffix,
        ) -
        getFieldValue('ssmt-mpers:AdjustmentsForDividendIncome', suffix) +
        getFieldValue(
          'ssmt-mpers:AdjustmentsForDepreciationExpense',
          suffix,
        ) +
        getFieldValue(
          'ssmt-mpers:AdjustmentsForAmortisationExpense',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:ImpairmentLossRecognisedInProfitOrLossIntangibleAssetsOtherThanGoodwill',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:ImpairmentLossRecognisedInProfitOrLossPropertyPlantAndEquipment',
          suffix,
        ) +
        getFieldValue('ifrs-smes:ReversalOfInventoryWritedown', suffix) +
        getFieldValue('ifrs-smes:AdjustmentsForProvisions', suffix) +
        getFieldValue(
          'ifrs-smes:AdjustmentsForUnrealisedForeignExchangeLossesGains',
          suffix,
        ) +
        getFieldValue('ifrs-smes:AdjustmentsForSharebasedPayments', suffix) +
        getFieldValue(
          'ifrs-smes:AdjustmentsForFairValueGainsLosses',
          suffix,
        ) -
        getFieldValue(
          'ssmt-mpers:AdjustmentsForShareOfProfitOfEquityAccountedAssociatesAndJointVenturesNetOfTax',
          suffix,
        ) -
        getFieldValue(
          'ifrs-smes:AdjustmentsForAccruedIncomeExpensesNotYetReceivedPaid',
          suffix,
        ) +
        getFieldValue('ifrs-smes:OtherAdjustmentsForNoncashItems', suffix) -
        getFieldValue(
          'ssmt-mpers:GainsLossesOnDisposalsOfPropertyPlantAndEquipment',
          suffix,
        ) -
        getFieldValue(
          'ssmt-mpers:GainsLossesOnDisposalsOfInvestmentProperties',
          suffix,
        ) -
        getFieldValue(
          'ssmt-mpers:GainsLossesOnDisposalOfOtherInvestments',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:OtherAdjustmentsForWhichCashEffectsAreInvestingOrFinancingCashFlow',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:OtherAdjustmentsToReconcileProfitLoss',
          suffix,
        );
      setValue(
        'ifrs-smes:AdjustmentsForReconcileProfitLoss',
        suffix,
        totalAdjustments,
      );

      // 3. Cash Generated from Operations
      const cashGenerated = aggregateProfitLoss + totalAdjustments;
      setValue(
        'ssmt-mpers:CashFlowsFromUsedInOperations',
        suffix,
        cashGenerated,
      );

      // 4. Net Cash from Operating Activities
      const netCashFromOperating =
        cashGenerated +
        getFieldValue(
          'ifrs-smes:DividendsPaidClassifiedAsOperatingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:DividendsReceivedClassifiedAsOperatingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:InterestPaidClassifiedAsOperatingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:InterestReceivedClassifiedAsOperatingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:IncomeTaxesPaidRefundClassifiedAsOperatingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:OtherInflowsOutflowsOfCashClassifiedAsOperatingActivities',
          suffix,
        );
      setValue(
        'ifrs-smes:CashFlowsFromUsedInOperatingActivities',
        suffix,
        netCashFromOperating,
      );

      // 5. Net Cash from Investing Activities (sum of 19 investing fields)
      // Note: Fields with Weight -1 in taxonomy are subtracted
      const netCashFromInvesting =
        getFieldValue(
          'ifrs-smes:CashFlowsFromLosingControlOfSubsidiariesOrOtherBusinessesClassifiedAsInvestingActivities',
          suffix,
        ) -
        getFieldValue(
          'ifrs-smes:CashFlowsUsedInObtainingControlOfSubsidiariesOrOtherBusinessesClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:OtherCashReceiptsFromSalesOfInterestsInJointVenturesClassifiedAsInvestingActivities',
          suffix,
        ) -
        getFieldValue(
          'ifrs-smes:OtherCashPaymentsToAcquireInterestsInJointVenturesClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:OtherCashReceiptsFromSalesOfEquityOrDebtInstrumentsOfOtherEntitiesClassifiedAsInvestingActivities',
          suffix,
        ) -
        getFieldValue(
          'ifrs-smes:OtherCashPaymentsToAcquireEquityOrDebtInstrumentsOfOtherEntitiesClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:ProceedsFromSalesOfPropertyPlantAndEquipmentClassifiedAsInvestingActivities',
          suffix,
        ) -
        getFieldValue(
          'ifrs-smes:PurchaseOfPropertyPlantAndEquipmentClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:ProceedsFromSalesOfIntangibleAssetsClassifiedAsInvestingActivities',
          suffix,
        ) -
        getFieldValue(
          'ifrs-smes:PurchaseOfIntangibleAssetsClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:ProceedsFromOtherLongtermAssetsClassifiedAsInvestingActivities',
          suffix,
        ) -
        getFieldValue(
          'ifrs-smes:PurchaseOfOtherLongtermAssetsClassifiedAsInvestingActivities',
          suffix,
        ) -
        getFieldValue(
          'ifrs-smes:CashAdvancesAndLoansMadeToOtherPartiesClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:CashReceiptsFromRepaymentOfAdvancesAndLoansMadeToOtherPartiesClassifiedAsInvestingActivities',
          suffix,
        ) -
        getFieldValue(
          'ifrs-smes:CashPaymentsForFutureContractsForwardContractsOptionContractsAndSwapContractsClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:CashReceiptsFromFutureContractsForwardContractsOptionContractsAndSwapContractsClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:DividendsReceivedClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:InterestReceivedClassifiedAsInvestingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:OtherInflowsOutflowsOfCashClassifiedAsInvestingActivities',
          suffix,
        );
      setValue(
        'ifrs-smes:CashFlowsFromUsedInInvestingActivities',
        suffix,
        netCashFromInvesting,
      );

      // 6. Net Cash from Financing Activities (sum of 11 financing fields)
      // Note: Fields with Weight -1 in taxonomy are subtracted
      const netCashFromFinancing =
        getFieldValue('ifrs-smes:ProceedsFromIssuingShares', suffix) +
        getFieldValue(
          'ifrs-smes:ProceedsFromIssuingOtherEquityInstruments',
          suffix,
        ) -
        getFieldValue(
          'ifrs-smes:PaymentsToAcquireOrRedeemEntitysShares',
          suffix,
        ) -
        getFieldValue('ifrs-smes:PaymentsOfOtherEquityInstruments', suffix) +
        getFieldValue(
          'ifrs-smes:ProceedsFromBorrowingsClassifiedAsFinancingActivities',
          suffix,
        ) -
        getFieldValue(
          'ifrs-smes:RepaymentsOfBorrowingsClassifiedAsFinancingActivities',
          suffix,
        ) -
        getFieldValue('ssmt-mpers:RepaymentOfLoanFromAssociate', suffix) -
        getFieldValue(
          'ifrs-smes:PaymentsOfFinanceLeaseLiabilitiesClassifiedAsFinancingActivities',
          suffix,
        ) -
        getFieldValue(
          'ifrs-smes:DividendsPaidClassifiedAsFinancingActivities',
          suffix,
        ) -
        getFieldValue(
          'ifrs-smes:InterestPaidClassifiedAsFinancingActivities',
          suffix,
        ) +
        getFieldValue(
          'ifrs-smes:OtherInflowsOutflowsOfCashClassifiedAsFinancingActivities',
          suffix,
        );
      setValue(
        'ifrs-smes:CashFlowsFromUsedInFinancingActivities',
        suffix,
        netCashFromFinancing,
      );

      // 7. Net Increase/Decrease Before Exchange Rate Effects
      const netIncreaseBeforeExchange =
        netCashFromOperating + netCashFromInvesting + netCashFromFinancing;
      setValue(
        'ifrs-smes:IncreaseDecreaseInCashAndCashEquivalentsBeforeEffectOfExchangeRateChanges',
        suffix,
        netIncreaseBeforeExchange,
      );

      // 8. Net Increase/Decrease After Exchange Rate Effects
      const exchangeRateEffect = getFieldValue(
        'ifrs-smes:EffectOfExchangeRateChangesOnCashAndCashEquivalents',
        suffix,
      );
      const netIncreaseAfterExchange =
        netIncreaseBeforeExchange + exchangeRateEffect;
      setValue(
        'ifrs-smes:IncreaseDecreaseInCashAndCashEquivalents',
        suffix,
        netIncreaseAfterExchange,
      );

      // 9. Cash and Cash Equivalents Details (reconciliation)
      // Calculate CashAndCashEquivalents:End_occurrence2 from details
      // Formula: Cash and Bank Balances - Bank Overdrafts + Other Adjustments
      const cashAndBank = getFieldValue('ssmt:CashAndBankBalances', suffix);
      const bankOverdraft = getFieldValue(
        'ifrs-smes:BankOverdraftsClassifiedAsCashEquivalents',
        suffix,
      );
      const otherAdjustments = getFieldValue(
        'ssmt:OtherAdjustmentsToReconcileCashAndCashEquivalents',
        suffix,
      );
      const cashEquivalentsDetails =
        cashAndBank - bankOverdraft + otherAdjustments;
      setValue(
        'ifrs-smes:CashAndCashEquivalents:End_occurrence2',
        suffix,
        cashEquivalentsDetails,
      );
    }
  });

  return calculatedTotals;
}

// ─── Role 19 (610000 — Statement of Changes in Equity) ─────────────────
//
// Lifted from `role19-610000-statement-of-changes-in-equity.component.ts`
// for the AS-842 silent-validation calc-diff branch. Mirrors the
// component's `calculateEquityColumnTotals` (lines 1747-1817) and
// `calculateDualColumnTotals` (lines 1823-1887) but takes a
// `Record<string, any>` instead of an Angular `FormGroup`.
//
// The component remains the source of truth for the editor flow — it
// continues to call its own private methods. This pure version is only
// invoked by `role19-statement-of-changes-in-equity.validator.ts` to
// produce the recomputed-totals snapshot the diff helper compares
// against the persisted N8N-extracted slice.
//
// Table-count permutation (the careful one — see AS-842 §12.4):
//   Visibility flags `showConsolidatedColumns` × `hasPreviousYear` come
//   from filing prerequisites (Role 0). They produce 1 / 2 / 4 visible
//   tables. The pure version iterates only visible (consolidationType ×
//   periodType) tuples to avoid producing recomputed keys that don't
//   exist in persisted data — matching what the user would see if they
//   opened the editor.
//
// Two layout modes (don't conflate):
//   - Equity-columns mode: schema declares `equityColumns`; computed
//     totals are keyed `{rowQname}:{columnQname}[{C}][{P}Year]`.
//   - Dual-column (legacy) mode: 4 fixed columns, computed keys are
//     `{qname}{[C][PYear]}` and BOTH `ifrs-full:` and `ifrs-smes:`
//     variants are written for the calculated rows (matching component
//     lines 1832-1833 etc.). The diff helper skips keys whose
//     persisted side is null, so writing both is safe.
